import { useId } from "react";
import { NumberField, NumberFieldInputProps } from "@base-ui/react/number-field";
import { Minus, MoveHorizontal, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import Label from "../label";
import Description from "../description";
import FieldError from "../field-error";
import "./style.scss";

export interface NumberInputProps extends Omit<NumberFieldInputProps, "placeholder"> {
	label?: string;
	hideLabel?: boolean;
	description?: string | null;
	error?: string;
	placeholder?: string | number | null;
	containerClassName?: string;
}

const NumberInput: React.FC<NumberInputProps> = ({ label, hideLabel, description, error, placeholder, containerClassName, ...props }) => {
	const generatedId = useId();
	const id = props.id ?? generatedId;

	return (
		<NumberField.Root id={id} className={cn("flex flex-col items-start gap-2", containerClassName)}>
			<NumberField.ScrubArea className="cursor-ew-resize leading-none">
				{label && (
					<Label variant="native" htmlFor={id} required={props.required} visible={!hideLabel} className="cursor-ew-resize">
						{label}
					</Label>
				)}

				<NumberField.ScrubAreaCursor>
					<MoveHorizontal />
				</NumberField.ScrubAreaCursor>
			</NumberField.ScrubArea>

			<NumberField.Group className="flex w-full">
				<NumberField.Decrement className="ms-number-addon ms-number-addon-left border-r-0! flex items-center justify-center bg-clip-padding select-none">
					<Minus size={14} />
				</NumberField.Decrement>

				<NumberField.Input
					id={id}
					name={props.name}
					placeholder={placeholder?.toString()}
					className="ms-input flex-1 min-w-0 w-0 text-center text-base tabular-nums focus:z-1 focus:outline-2 focus:-outline-offset-1 rounded-none!"
				/>

				<NumberField.Increment className="ms-number-addon ms-number-addon-right border-l-0! flex items-center justify-center bg-clip-padding select-none">
					<Plus size={14} />
				</NumberField.Increment>
			</NumberField.Group>

			{description && <Description>{description}</Description>}

			<FieldError message={error} />
		</NumberField.Root>
	);
};

export default NumberInput;
